import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'

import type { InputHTMLAttributes, ReactElement } from 'react'
import type { IHoliday } from '~/types/date'

import DateInputHeader, { TABULATION_NAME } from './Header'
import MainInput from './MainInput'
import TimeInput from './TimeInput'

import Button from '~/components/Buttons/Button'
import dayjs from '~/utils/dayjs'

import 'react-datepicker/dist/react-datepicker.css'
import './styles.scss'

const dateFilterValues = [7, 30, 90, 180, 365]

const TODAY = dayjs()

const YESTERDAY = dayjs().subtract(1, 'day')

export type Placement =
  | 'top-start'
  | 'top-end'
  | 'top'
  | 'right-start'
  | 'right-end'
  | 'right'
  | 'bottom-start'
  | 'bottom-end'
  | 'bottom'
  | 'left-start'
  | 'left-end'
  | 'left'

export interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  autoFocus?: boolean
  calendarStartDay?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  calendarStartOpen?: boolean
  className?: string
  dateValue?: string
  dayClassName?: (date: Date) => string
  disabled?: boolean
  endDate?: string
  excludeToday?: boolean
  highlightDates?: string[]
  holidays?: IHoliday[]
  label?: string
  labelPosition?: 'top' | 'left'
  mainInputClassName?: string
  maxDate?: Date
  maxRangeDays?: number
  minDate?: Date
  onCalendarClose?: VoidFunction
  onCalendarOpen?: VoidFunction
  onDateChange?: (date?: string) => void
  onDateRangeChange?: (dates: string[]) => void
  placeholder?: string
  placement?: Placement
  required?: boolean
  shouldCloseOnSelect?: boolean
  startDate?: string
  withFilters?: boolean
  withRange?: boolean
  withTime?: boolean
}

const DateInput = ({
  autoFocus,
  calendarStartDay,
  calendarStartOpen,
  className,
  dateValue,
  dayClassName,
  disabled,
  endDate: endDateProp,
  excludeToday,
  highlightDates,
  holidays,
  label,
  labelPosition,
  mainInputClassName,
  maxDate,
  maxRangeDays,
  minDate,
  onCalendarClose,
  onCalendarOpen,
  onDateChange,
  onDateRangeChange,
  placeholder,
  placement,
  required,
  shouldCloseOnSelect = true,
  startDate: startDateProp,
  withFilters,
  withRange,
  withTime,
}: Props): ReactElement => {
  /** Local state */

  const ref = useRef<HTMLInputElement>(null)

  let datepickerRef = useRef(null)

  const [date, setDate] = useState(dateValue)

  const [errorMessage, setErrorMessage] = useState<string | null>()

  const [startDate, setStartDate] = useState(startDateProp)

  const [endDate, setEndDate] = useState(endDateProp)

  const [rangeCustomValues, setRangeCustomValues] = useState<string[]>([])

  const [currentFilter, setCurrentFilter] = useState(TABULATION_NAME.CLASSIC)

  const classes = classNames('date-input-component', className)

  const mainInputClasses = classNames(mainInputClassName, { 'with-time': withTime })

  const calendarClasses = classNames('date-input-calendar', {
    'with-filters': withFilters,
    'with-range': withRange,
    'with-time': withTime,
    'without-children': !withRange && !withFilters,
  })

  const startDateValue = startDate ? new Date(startDate) : null

  const endDateValue = endDate ? new Date(endDate) : null

  const value = date ? new Date(date) : null

  const validRange = useCallback(
    (start?: string, end?: string) => {
      if (maxRangeDays && start && end) {
        const dayjsStartDate = dayjs(start)
        const dayjsEndDate = dayjs(end)

        return maxRangeDays && Math.abs(dayjsStartDate.diff(dayjsEndDate, 'days')) <= maxRangeDays
      }
      return true
    },
    [maxRangeDays]
  )

  /** Handlers */

  const handleTimeChange = (time: string, date: Date | null): void => {
    const [hh, mm, ss] = time.split(':')
    const targetDate = date || new Date()

    targetDate.setHours(+hh || 0, +mm || 0, +ss || 0)

    setDate(targetDate.toISOString())
    onDateChange && onDateChange(targetDate.toISOString())
  }

  const handleChange = (date: Date | (Date | null)[] | null) => {
    setErrorMessage(null)

    if (withRange) {
      const [start, end] = date as (Date | null)[]

      if (start && end) {
        // NOTE: start & end

        setStartDate(start?.toISOString())
        setEndDate(end.toISOString())
      } else if (start && endDate) {
        // NOTE: only start
        setStartDate(start.toISOString())

        if (currentFilter === TABULATION_NAME.SINCE) {
          setEndDate(excludeToday ? YESTERDAY.toISOString() : TODAY.toISOString())
        } else {
          setEndDate(undefined)
        }
      } else if (start) {
        // NOTE: end & start
        if (currentFilter === TABULATION_NAME.SINCE) {
          setEndDate(excludeToday ? YESTERDAY.toISOString() : TODAY.toISOString())
        } else {
          setEndDate(startDate)
        }
        setStartDate(start.toISOString())
      }
    } else {
      setDate((date as Date | null)?.toISOString())
      onDateChange && onDateChange((date as Date | null)?.toISOString())
    }
  }

  /**
   * If a `maxRangeDays` props is provided and a `startDate` is selected, it checks that `date` is prior or equal to `maxDate`, and posterior or equal to `minDate`.
   * If they were not provided, it returns true.
   * @param date A Day.js date
   */
  const isMaxRangeRespected = (date: dayjs.Dayjs): boolean => {
    if (maxRangeDays && startDate) {
      const maxDate = dayjs(startDate).add(maxRangeDays, 'days')
      const minDate = dayjs(startDate).subtract(maxRangeDays, 'days')

      return (date.isBefore(maxDate) || date.isSame(maxDate)) && (date.isAfter(minDate) || date.isSame(minDate))
    }

    return true
  }

  /**
   * It takes a string and checks whether it respects the YYYY-MM-DD date format
   * @param date: the string we want to test
   * @param maxDate (optional): if the first argument is indeed a date, it must be prior to that `maxDate`
   */
  const isDateValid = (date: string, maxDate?: string | Date | null): boolean =>
    Boolean(
      new Date(date).toString() !== 'Invalid Date' &&
        dayjs(date, 'YYYY-MM-DD', true).isValid() &&
        date.match(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/) &&
        (!maxDate || (maxDate && dayjs(date).isBefore(maxDate)))
    )

  //-- NOTE: custom inputs
  const handleStartDateChange = (date?: string) => {
    const dayjsDate = dayjs(date)

    setErrorMessage(null)

    if (
      date &&
      isDateValid(
        date,
        currentFilter === TABULATION_NAME.SINCE
          ? excludeToday
            ? YESTERDAY.format('YYYY-MM-DD')
            : TODAY.format('YYYY-MM-DD')
          : null
      )
    ) {
      // NOTE: use to scroll to the selected day, not present in type declarations
      // @ts-expect-error setSelected does not exist on Mutable<Ref>
      datepickerRef.setSelected(new Date(date), null, true)

      setStartDate(dayjsDate.toISOString())
    }

    if (endDate) {
      setEndDate(endDate)
    }
  }

  const handleEndDateChange = (date?: string) => {
    setErrorMessage(null)

    if (date && isDateValid(date)) {
      // NOTE: use to scroll to the selected day, not present in type declarations
      // @ts-expect-error setSelected does not exist on Mutable<Ref>
      datepickerRef.setSelected(new Date(date), null, true)

      setStartDate(startDate)
      setEndDate(date)
    }
  }

  const handleSelectLastXDays = (value: number) => () => {
    const endDate = dayjs(excludeToday ? YESTERDAY : TODAY).format('YYYY-MM-DD')
    const startDate = dayjs(endDate)
      .subtract(value - 1, 'days')
      .format('YYYY-MM-DD')

    // NOTE: use to scroll to the selected day, not present in type declarations
    // @ts-expect-error setSelected does not exist on Mutable<Ref>
    datepickerRef.setSelected(new Date(endDate), null, true)

    setStartDate(startDate)
    setEndDate(endDate)
  }

  const handleTabChange = (currentTab: TABULATION_NAME) => {
    setCurrentFilter(currentTab)
  }

  const handleConfirm = () => {
    if (startDate && endDate) {
      setRangeCustomValues([startDate, endDate])
      onDateRangeChange && onDateRangeChange([startDate, endDate])
      // @ts-expect-error setOpen does not exist on Mutable<Ref>, not present in type declarations
      datepickerRef.setOpen(false)
    }
  }

  const handleCancel = () => {
    setStartDate(rangeCustomValues[0])
    setEndDate(rangeCustomValues[1])
    // @ts-expect-error setOpen does not exist on Mutable<Ref>, not present in type declarations
    datepickerRef.setOpen(false)
  }

  const handleCalendarClose = () => {
    // NOTE: we need to set it on close because we can't determine a close action
    // from clickoutside or from cancel/confirm
    if (withRange) {
      setStartDate(rangeCustomValues[0])
      setEndDate(rangeCustomValues[1])
    }
    onCalendarClose && onCalendarClose()
  }

  const filterDatesBeforeToday = (date: Date) => {
    const dayjsDate = dayjs(date)
    const lastDay = excludeToday ? YESTERDAY : TODAY
    return (dayjsDate.isBefore(lastDay) || dayjsDate.isSame(lastDay)) && isMaxRangeRespected(dayjsDate)
  }

  const defaultFilter = (date: Date) => {
    return isMaxRangeRespected(dayjs(date))
  }

  /** Effects */

  useEffect(() => {
    if (startDateProp) {
      setStartDate(startDateProp)
    }
    if (endDateProp) {
      setEndDate(endDateProp)
    }
    if (startDateProp && endDateProp) {
      setRangeCustomValues([startDateProp, endDateProp])
    }
  }, [endDateProp, startDateProp, setRangeCustomValues])

  useEffect(() => {
    if (withRange && startDate && endDate && !validRange(startDate, endDate)) {
      setErrorMessage(`You can't select a period longer than ${maxRangeDays} days`)
    } else {
      setErrorMessage(null)
    }
  }, [endDate, maxRangeDays, startDate, validRange, withRange])

  return (
    <ReactDatePicker
      autoFocus={autoFocus}
      calendarClassName={calendarClasses}
      className={classes}
      customInput={
        <MainInput
          className={mainInputClasses}
          customValues={rangeCustomValues}
          disabled={disabled}
          label={label}
          labelPosition={labelPosition}
          ref={ref}
          required={required}
          withRange={withRange}
        />
      }
      // @ts-expect-error datepickerRef is a constant and cannot be assign
      ref={(r) => (datepickerRef = r)}
      {...(withTime
        ? {
            customTimeInput: <TimeInput dateValue={value} onTimeChange={handleTimeChange} />,
          }
        : {})}
      {...(withRange
        ? {
            endDate: endDateValue,
            selectsRange: true,
            startDate: startDateValue,
          }
        : {})}
      calendarStartDay={calendarStartDay}
      dateFormat={withRange ? 'MMMM dd, yyyy' : withTime ? 'yyyy-MM-dd @ HH:mm:ss' : 'yyyy-MM-dd'}
      dayClassName={dayClassName}
      disabled={disabled}
      disabledKeyboardNavigation
      filterDate={currentFilter === TABULATION_NAME.SINCE ? filterDatesBeforeToday : defaultFilter}
      fixedHeight
      focusSelectedMonth={false}
      highlightDates={highlightDates?.map((el) => new Date(el))}
      holidays={holidays}
      maxDate={maxDate}
      minDate={minDate}
      monthsShown={1}
      onCalendarClose={handleCalendarClose}
      onCalendarOpen={onCalendarOpen}
      onChange={handleChange}
      placeholderText={placeholder}
      popperPlacement={placement}
      portalId="datepicker-portal"
      renderCustomHeader={({ changeMonth, changeYear, date, decreaseMonth, increaseMonth }) => (
        <DateInputHeader
          changeMonth={changeMonth}
          changeYear={changeYear}
          date={date}
          decreaseMonth={decreaseMonth}
          defaultTab={currentFilter}
          endDate={endDate ? dayjs(endDate).format('YYYY-MM-DD') : ''}
          increaseMonth={increaseMonth}
          onEndDateChange={handleEndDateChange}
          onStartDateChange={handleStartDateChange}
          onTabChange={handleTabChange}
          startDate={startDate ? dayjs(startDate).format('YYYY-MM-DD') : ''}
          withFilters={withFilters}
          withRange={withRange}
        />
      )}
      required={required}
      selected={withRange ? startDateValue : value}
      // @ts-expect-error cannot assign 'boolean | undefined' to 'true | undefined'
      selectsMultiple={withRange}
      shouldCloseOnSelect={shouldCloseOnSelect && !withTime && !withRange}
      showPopperArrow={false}
      showTimeInput={withTime}
      startOpen={calendarStartOpen}
      timeFormat="HH:mm"
    >
      {withRange ? (
        <div>
          <span className="error-message">{errorMessage}</span>
          <div className="confirm-btns-container">
            <Button ghost onClick={handleCancel}>
              Cancel
            </Button>
            <Button disabled={!(startDate && endDate) || !!errorMessage} onClick={handleConfirm}>
              Apply
            </Button>
          </div>
        </div>
      ) : null}
      {withFilters && currentFilter === TABULATION_NAME.CLASSIC ? (
        <div className="advanced-filters">
          {dateFilterValues.map((value) => (
            <Button ghost key={`filter-last-${value}-days`} onClick={handleSelectLastXDays(value)}>
              Last {`${value}`} days
            </Button>
          ))}
        </div>
      ) : null}
    </ReactDatePicker>
  )
}

export default DateInput
